import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const formRef = useRef<HTMLFormElement>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus('idle');
        setErrorMsg('');

        if (!formRef.current) return;

        const formData = new FormData(formRef.current);
        const data = {
            name: formData.get('name'),
            email: formData.get('email'),
            message: formData.get('message')
        };

        try {
            const api = process.env.API_URL || 'http://localhost:3001';
            const res = await fetch(`${api}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });

            const result = await res.json();

            if (res.ok && result.success) {
                setStatus('success');
                formRef.current.reset();
                setTimeout(() => {
                    onClose();
                    setStatus('idle');
                }, 2500);
            } else {
                setStatus('error');
                setErrorMsg(result.error || 'Bir hata oluştu.');
            }
        } catch (err) {
            setStatus('error');
            setErrorMsg('Sunucu hatası.');
        } finally {
            setIsLoading(false);
        }
    };

    // Updated Input Style based on User Screenshot: Icon + Label + Input specific
    const InputGroup = ({ label, name, type = "text", icon, required = false, isTextArea = false }: any) => (
        <div className="group flex items-start gap-4 bg-[#050505] border-2 border-[#222] rounded-2xl p-4 transition-all duration-300 focus-within:border-emerald-500/50 focus-within:bg-black focus-within:shadow-[0_0_20px_rgba(16,185,129,0.05)] h-full">
            <div className="mt-1 text-[#444] group-focus-within:text-emerald-500 transition-colors duration-300">
                {icon}
            </div>
            <div className="flex flex-col flex-1 min-w-0">
                <label htmlFor={name} className="text-[10px] font-bold text-[#555] tracking-widest uppercase mb-1 group-focus-within:text-emerald-500/70 transition-colors">
                    {label}
                </label>
                {isTextArea ? (
                    <textarea
                        id={name}
                        name={name}
                        required={required}
                        rows={3}
                        className="bg-transparent border-none outline-none text-white font-bold text-sm p-0 w-full resize-none placeholder-[#333]"
                        placeholder="..."
                    />
                ) : (
                    <input
                        id={name}
                        name={name}
                        type={type}
                        required={required}
                        className="bg-transparent border-none outline-none text-white font-bold text-sm p-0 w-full placeholder-[#333]"
                        placeholder="..."
                    />
                )}
            </div>
        </div>
    );

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05 // Faster stagger
            }
        },
        exit: { opacity: 0 }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "circOut" } }, // Smooth slide, no bounce
        exit: { opacity: 0, y: -10 }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Dark Blurred Backdrop - Lighter blur as requested */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
                    />

                    {/* Content Container (No Background!) */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="relative w-full max-w-[540px] z-10 flex flex-col gap-4 pointer-events-none"
                    >
                        {/* Status Overlay */}
                        {status === 'success' && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="absolute inset-0 z-50 flex flex-col items-center justify-center pointer-events-auto"
                            >
                                <div className="bg-[#0a0a0a] border-2 border-emerald-500/50 p-8 rounded-3xl flex flex-col items-center shadow-[0_0_50px_rgba(16,185,129,0.3)] w-full max-w-xs text-center">
                                    <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4 text-emerald-500 border-2 border-emerald-500/20">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-1">MESAJ GÖNDERİLDİ</h3>
                                    <p className="text-xs text-[#666] font-medium uppercase tracking-wider">En kısa sürede dönüş yapacağım.</p>
                                </div>
                            </motion.div>
                        )}

                        <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">

                            {/* Grid Row: Name & Email */}
                            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-3 pointer-events-auto">
                                <InputGroup
                                    label="İSİM SOYİSİM"
                                    name="name"
                                    required
                                    icon={
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    }
                                />
                                <InputGroup
                                    label="E-POSTA ADRESİ"
                                    name="email"
                                    type="email"
                                    required
                                    icon={
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                        </svg>
                                    }
                                />
                            </motion.div>

                            <motion.div variants={itemVariants} className="pointer-events-auto">
                                <InputGroup
                                    label="MESAJINIZ"
                                    name="message"
                                    isTextArea
                                    required
                                    icon={
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                        </svg>
                                    }
                                />
                            </motion.div>

                            {status === 'error' && (
                                <motion.div variants={itemVariants} className="pointer-events-auto text-red-500 text-xs font-bold bg-red-500/10 p-3 rounded-xl border-2 border-red-500/20 flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    {errorMsg}
                                </motion.div>
                            )}

                            <motion.div variants={itemVariants} className="flex items-stretch gap-3 mt-2 pointer-events-auto">
                                {/* Close Button (Small Red X) */}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="group flex items-center justify-center w-14 bg-red-500/10 hover:bg-red-500/20 border-2 border-red-500/50 hover:border-red-500 text-red-500 rounded-2xl transition-all duration-300 active:scale-95"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>

                                {/* Submit Button (No Arrow, Flex-1) */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="flex-1 group relative overflow-hidden bg-emerald-500/10 hover:bg-emerald-500/20 border-2 border-emerald-500 text-emerald-500 font-bold text-sm py-4 rounded-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 tracking-widest uppercase"
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                                            <span>GÖNDERİLİYOR...</span>
                                        </div>
                                    ) : (
                                        "MESAJI GÖNDER"
                                    )}
                                </button>
                            </motion.div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
